// src/components/Pages/BriefBuilderPage.js
import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    Button,
    Container,
    Group,
    Paper,
    Select,
    Stack,
    Text,
    Textarea,
    Title,
    Loader,
    Alert,
    JsonInput,
    useMantineTheme,
    useMantineColorScheme,
    Divider, // Keep Divider as it's used for the results section title
} from '@mantine/core';
import { IconAlertCircle, IconFileTextAi, IconLink } from '@tabler/icons-react';
import { useSearchParams } from 'react-router-dom';
import { useBackgroundImage } from '../../context/BackgroundImageContext';
import backgroundImageFile from '../../assets/images/brochure-background.jpg';
import { useAuth } from '../../context/AuthContext';
import BriefDisplay from '../Briefs/BriefDisplay'; // Correct import path

// Data for input dropdowns
const targetBrands = ["LoveMoney", "LoveProperty", "LoveFood", "LoveExploring", "General"];
const targetRegions = ["United Kingdom", "United States", "Australia", "Canada", "Global"];

function BriefBuilderPage() {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const { isBackgroundImageVisible } = useBackgroundImage();
    const { currentUser } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    // Input states
    const [rawTitleIdea, setRawTitleIdea] = useState('');
    const [targetBrand, setTargetBrand] = useState(targetBrands[0]);
    const [targetRegion, setTargetRegion] = useState(targetRegions[0]);
    const [relevantSourceURLsInput, setRelevantSourceURLsInput] = useState('');

    // API interaction states
    const [isLoading, setIsLoading] = useState(false);
    const [apiResponse, setApiResponse] = useState(null);
    const [apiError, setApiError] = useState(null);

    // useEffect to handle incoming query parameters
    useEffect(() => {
        const initialTitle = searchParams.get('initialTitle');
        const initialUrls = searchParams.get('initialUrls');
        const initialBrand = searchParams.get('targetBrand'); // <<< NEW: Get the brand

        if (initialTitle) {
            setRawTitleIdea(initialTitle);
        }

        if (initialUrls) {
            setRelevantSourceURLsInput(initialUrls);
        }

        // <<< NEW: Check if the brand exists and is valid, then set it
        if (initialBrand && targetBrands.includes(initialBrand)) {
            setTargetBrand(initialBrand);
        }

        // Clean up all parameters from the URL
        if (initialTitle || initialUrls || initialBrand) {
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('initialTitle');
            newSearchParams.delete('initialUrls');
            newSearchParams.delete('targetBrand'); // <<< NEW: Clean up the brand
            setSearchParams(newSearchParams, { replace: true });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, setSearchParams]);

    const briefBuilderApiUrl = 'https://generate-writer-brief-u6n4kc7zyq-ew.a.run.app';

    const handleGenerateBrief = useCallback(async (event) => {
        if (event) event.preventDefault();

        if (!rawTitleIdea.trim()) {
            setApiError("Please enter a title or idea.");
            return;
        }
        if (!currentUser) {
            setApiError("User not authenticated. Please log in again.");
            return;
        }

        setIsLoading(true);
        setApiResponse(null);
        setApiError(null);

        let idToken;
        try {
            idToken = await currentUser.getIdToken();
        } catch (tokenError) {
            console.error("BriefBuilderPage: Error getting ID token:", tokenError);
            setApiError("Could not verify user session. Please log in again.");
            setIsLoading(false);
            return;
        }

        const processedURLs = relevantSourceURLsInput
            .split('\n')
            .map(url => url.trim())
            .filter(url => url.length > 0);

        const payload = {
            raw_title_idea: rawTitleIdea,
            target_brand: targetBrand,
            target_region: targetRegion,
            relevant_external_source_urls: processedURLs,
        };

        console.log("BriefBuilderPage: Sending payload:", payload);

        try {
            const response = await fetch(briefBuilderApiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify(payload),
            });

            const responseData = await response.json();

            if (!response.ok) {
                const errorMessage = responseData?.message || responseData?.error || `HTTP error! status: ${response.status}`;
                console.error("BriefBuilderPage: API Error Response Data:", responseData);
                throw new Error(errorMessage);
            }

            setApiResponse(responseData);
            console.log("BriefBuilderPage: Brief generation successful.", responseData);

        } catch (error) {
            console.error("BriefBuilderPage: API call error:", error);
            setApiError(error.message || "Failed to generate brief from API.");
        } finally {
            setIsLoading(false);
        }
    }, [rawTitleIdea, targetBrand, targetRegion, relevantSourceURLsInput, currentUser]);

    // Theme-aware styles
    const sectionContainerBg = isDark ? theme.colors.dark[6] : theme.colors.gray[0];
    const textOnBackgroundImageColor = isBackgroundImageVisible ? theme.white : (isDark ? theme.white : theme.black);

    const inputStyles = (theme, isDark) => ({
        input: {
            backgroundColor: isDark ? theme.colors.dark[4] : theme.colors.gray[4],
            color: isDark ? theme.white : theme.black,
            borderColor: isDark ? theme.colors.dark[4] : theme.colors.gray[4],
            '&::placeholder': {
                color: isDark ? theme.colors.dark[2] : theme.colors.gray[6],
            },
            '&:focus, &:focus-within': {
                borderColor: theme.colors.blue[isDark ? 7 : 5],
            },
        }
    });

    const currentInputStyles = inputStyles(theme, isDark);

    return (
        <Box
            style={{
                width: '100%',
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                backgroundImage: isBackgroundImageVisible ? `url(${backgroundImageFile})` : 'none',
                backgroundColor: !isBackgroundImageVisible ? (isDark ? theme.colors.dark[7] : theme.colors.gray[0]) : 'transparent',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundAttachment: 'fixed',
                overflow: 'auto',
                scrollBehavior: 'smooth',
            }}
        >
            <Container size="lg" py="xl" style={{ width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Stack gap="md">
                    <Title order={1} ta="center" style={{ color: textOnBackgroundImageColor }}>
                        <Group justify="center" gap="xs">
                            <IconFileTextAi size={30} stroke={1.5} color={theme.colors.yellow[isDark || isBackgroundImageVisible ? 4 : 6]} />
                            Brief Builder
                        </Group>
                    </Title>
                    <Text ta="center" style={{ color: textOnBackgroundImageColor }}>
                        Generate a comprehensive writer's brief based on your idea and chosen scope.
                    </Text>

                    <Paper
                        withBorder shadow="md" p="xl" radius="md" mt="lg" component="form"
                        bg={sectionContainerBg}
                        onSubmit={handleGenerateBrief}
                        maw={theme.breakpoints.md}
                        mx="auto"
                        style={{ width: '100%' }}
                    >
                        <Stack gap="lg">
                            <Title order={2}>| Article Scope</Title>
                            <Textarea
                                size="md"
                                label="Title or Core Idea:"
                                placeholder="Enter the working title or a core concept for the article..."
                                value={rawTitleIdea}
                                onChange={(event) => setRawTitleIdea(event.currentTarget.value)}
                                autosize
                                minRows={2}
                                required
                                styles={currentInputStyles}
                            />
                            <Select
                                size="md"
                                label="Target Brand:"
                                data={targetBrands}
                                value={targetBrand}
                                onChange={setTargetBrand}
                                required
                                styles={currentInputStyles}
                            />
                            <Select
                                size="md"
                                label="Target Region:"
                                data={targetRegions}
                                value={targetRegion}
                                onChange={setTargetRegion}
                                required
                                styles={currentInputStyles}
                            />
                            <Textarea
                                size="md"
                                label="Relevant Source URLs (Optional):"
                                description="Enter one URL per line. These will be referenced in the brief."
                                placeholder="https://www.example.com/source1&#10;https://www.another.com/inspiration"
                                value={relevantSourceURLsInput}
                                onChange={(event) => setRelevantSourceURLsInput(event.currentTarget.value)}
                                autosize
                                minRows={3}
                                styles={currentInputStyles}
                                leftSection={<IconLink size="1rem" />}
                            />
                            <Button type="submit" loading={isLoading} fullWidth mt="md" size="lg" variant="gradient"
                                gradient={{ from: 'teal.7', to: 'lime.4', deg: 105 }}>
                                {isLoading ? 'Generating Brief...' : 'Generate Brief'}
                            </Button>
                        </Stack>
                    </Paper>

                    {apiError && (
                        <Alert title="Error" color="red" icon={<IconAlertCircle />} mt="md" withCloseButton onClose={() => setApiError(null)}>
                            <Stack gap="xs">
                                <Text fw={700}>{apiError}</Text>
                                <Text size="sm">
                                    Please try again in five minutes. (If the problem persists, contact the team.)
                                </Text>
                            </Stack>
                        </Alert>
                    )}
                    {isLoading && (
                        <Group justify="center" mt="lg">
                            <Loader />
                            <Text ml="sm" style={{ color: textOnBackgroundImageColor }}>
                                Considering title / variants / scope / guidelines / structure / style & tone... (~1min!)
                            </Text>
                        </Group>
                    )}

                    {apiResponse && apiResponse.generated_brief && (
                        <Box mt="xl">
                            <Divider
                                my="xl"
                                label={<Title order={2} style={{ color: textOnBackgroundImageColor }}>| Your Initial Brief |</Title>}
                                labelPosition="center"
                                style={{
                                    label: {
                                        color: textOnBackgroundImageColor,
                                        '&::before, &::after': { borderColor: isBackgroundImageVisible ? theme.colors.dark[0] : (isDark ? theme.colors.dark[3] : theme.colors.gray[3]) }
                                    }
                                }}
                            />
                            <BriefDisplay brief={apiResponse.generated_brief} />
                            {apiResponse.debug_timings && (
                                <Box mt="lg">
                                    <Title order={4} mb="xs" c="dimmed">API Debug Timings:</Title>
                                    <JsonInput
                                        value={JSON.stringify(apiResponse.debug_timings, null, 2)}
                                        readOnly
                                        formatOnBlur
                                        autosize
                                        minRows={3}
                                        styles={(theme) => ({
                                            input: {
                                                fontFamily: theme.fontFamilyMonospace,
                                                fontSize: theme.fontSizes.xs,
                                                backgroundColor: isDark ? theme.colors.dark[8] : theme.white,
                                                color: isDark ? theme.colors.gray[4] : theme.colors.dark[5],
                                            },
                                        })}
                                    />
                                </Box>
                            )}
                        </Box>
                    )}
                </Stack>
            </Container>
        </Box>
    );
}

export default BriefBuilderPage;